import { getDefaultClient } from '@dynamic-labs-sdk/client';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';
import type { Transaction, VersionedTransaction } from '@solana/web3.js';

import type { SolanaWalletAccount } from '../SolanaWalletAccount.types';
import { NotSolanaProviderError } from '../errors/NotSolanaProviderError';
import { isSolanaWalletProvider } from '../isSolanaWalletProvider';

type SignAllTransactionsParams = {
  transactions: (Transaction | VersionedTransaction)[];
  walletAccount: SolanaWalletAccount;
};

/**
 * Signs multiple Solana transactions
 * @param params.transactions - The transactions to sign
 * @param params.walletAccount - The wallet account to sign the transactions with
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An object containing the signed transactions
 */
export const signAllTransactions = async (
  { walletAccount, transactions }: SignAllTransactionsParams,
  client = getDefaultClient()
): Promise<{ signedTransactions: (Transaction | VersionedTransaction)[] }> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isSolanaWalletProvider(provider)) {
    throw new NotSolanaProviderError({ providerKey: provider.key });
  }

  return provider.signAllTransactions({ transactions, walletAccount });
};
